import axios from 'axios';
import config from '@/config';
import { EventBus as bus } from '@/event-bus';
import interceptors from './interceptors';

const baseDomain = config.baseDomain;
const baseURL = `${baseDomain}/api/v1`;

const cache = {}; // Simple cache system for GET requests
const pendingRequests = {};
// Bumped by clearRequestCache so a response that was already in flight when the
// session changed can never land in the cache of the new one.
let cacheGeneration = 0;

// Cached GET responses are session data: `case/entities/find`, for instance,
// returns the merchant's own entity catalogue, whose ids only resolve under that
// merchant. Key every entry by the active merchant so an entry can never be
// served to another one.
function getCacheKey(url) {
	return `${sessionStorage.getItem('merchant_id') || ''}|${url}`;
}

// This cache lives in module memory, so it outlives every auth transition: log
// out is a router push, and impersonation login/reset swap the token in place -
// none of them reload the page. Without this reset a tab keeps serving the
// previous session's responses for the rest of their TTL (24h for the entity
// catalogue), which made person/company creates fail with "unable to get person
// entity" against the newly selected merchant. Called from stores/auth.ts on
// every session change.
export function clearRequestCache() {
	cacheGeneration += 1;

	Object.keys(cache).forEach((key) => {
		delete cache[key];
	});
	Object.keys(pendingRequests).forEach((key) => {
		delete pendingRequests[key];
	});
}

export default {
	post(url, payload) {
		let client = GetAxiosClient();
		return client.post(url, payload);
	},
	postWithAuth(url, payload, customHeaders, customConfig) {
		const client = GetAxiosClient();
		const authorizationHeader = {
			Authorization: 'Bearer ' + (sessionStorage.getItem('token') || ''),
		};
		let calculatedHeaders = customHeaders
			? { ...authorizationHeader, ...customHeaders }
			: authorizationHeader;
		return client.post(url, payload, {
			headers: calculatedHeaders,
			...customConfig,
		});
	},
	patchWithAuth(url, payload) {
		let client = GetAxiosClient();
		const authorizationHeader = {
			Authorization: 'Bearer ' + (sessionStorage.getItem('token') || ''),
		};
		return client.patch(url, payload, {
			headers: authorizationHeader,
		});
	},
	putWithAuth(url, payload, customHeaders, customConfig) {
		let client = GetAxiosClient();
		const authorizationHeader = {
			Authorization: 'Bearer ' + (sessionStorage.getItem('token') || ''),
		};
		let calculatedHeaders = customHeaders
			? { ...authorizationHeader, ...customHeaders }
			: authorizationHeader;
		return client.put(url, payload, {
			headers: calculatedHeaders,
			...customConfig,
		});
	},
	putWithAuthForm(url, payload, customHeaders) {
		let client = GetAxiosClientFORM();
		const authorizationHeader = {
			Authorization: 'Bearer ' + (sessionStorage.getItem('token') || ''),
		};
		let calculatedHeaders = customHeaders
			? { ...authorizationHeader, ...customHeaders }
			: authorizationHeader;
		return client.put(url, payload, {
			headers: calculatedHeaders,
		});
	},
	deleteWithAuth(url, payload, customHeaders, customConfig) {
		let client = GetAxiosClient();
		const authorizationHeader = {
			Authorization: 'Bearer ' + (sessionStorage.getItem('token') || ''),
		};
		let calculatedHeaders = customHeaders
			? { ...authorizationHeader, ...customHeaders }
			: authorizationHeader;
		return client.delete(url, {
			headers: calculatedHeaders,
			data: payload,
			...customConfig,
		});
	},
	getWithAuthAndCache(url, cacheTimeInSeconds = 5) {
		let calculatedHeaders = {
			Authorization: 'Bearer ' + (sessionStorage.getItem('token') || ''),
		};
		let client = GetAxiosClient();
		const key = getCacheKey(url);
		const generation = cacheGeneration;

		if (cache[key]) {
			return Promise.resolve(cache[key]);
		}

		if (pendingRequests[key]) {
			return pendingRequests[key];
		}

		const request = client
			.get(url, {
				headers: calculatedHeaders,
			})
			.then((response) => {
				delete pendingRequests[key];

				// The session changed while this was in flight; the response
				// belongs to the previous one, so hand it to the caller but keep
				// it out of the cache.
				if (generation !== cacheGeneration) {
					return response;
				}

				cache[key] = response;

				setTimeout(() => {
					if (generation === cacheGeneration) {
						delete cache[key];
					}
				}, cacheTimeInSeconds * 1000);

				return response;
			})
			.catch((error) => {
				delete pendingRequests[key];
				throw error;
			});

		pendingRequests[key] = request;
		return request;
	},
	getWithAuth(url, payload, customHeaders, axiosConfig) {
		const authorizationHeader = {
			Authorization: 'Bearer ' + (sessionStorage.getItem('token') || ''),
		};
		let calculatedHeaders = customHeaders
			? { ...authorizationHeader, ...customHeaders }
			: authorizationHeader;
		let client = GetAxiosClient();
		return client.get(url, {
			headers: calculatedHeaders,
			...axiosConfig,
		});
	},
	getWithAuthAndPayload(url, date, customHeaders) {
		let client = GetAxiosClient();
		const authorizationHeader = {
			Authorization: 'Bearer ' + (sessionStorage.getItem('token') || ''),
		};
		let calculatedHeaders = customHeaders
			? { ...authorizationHeader, ...customHeaders }
			: authorizationHeader;
		return client.get(url, {
			headers: calculatedHeaders,
			params: {
				date: date,
			},
		});
	},
	getWithoutAuth(url) {
		let client = GetAxiosClient();
		return client.get(url);
	},
	//download zip using arraybuffer
	postWithAuthForZip(url, payload) {
		let client = GetAxiosClient();
		const authorizationHeader = {
			Authorization: 'Bearer ' + (sessionStorage.getItem('token') || ''),
		};
		return client.post(url, payload, {
			headers: authorizationHeader,
			responseType: 'arraybuffer',
		});
	},
	// download zip from response
	postWithAuthForZipFromResponse(url, payload, customHeaders) {
		let client = GetAxiosClient();
		const authorizationHeader = {
			Authorization: 'Bearer ' + (sessionStorage.getItem('token') || ''),
		};
		let calculatedHeaders = customHeaders
			? { ...authorizationHeader, ...customHeaders }
			: authorizationHeader;
		return client.post(url, payload, {
			headers: calculatedHeaders,
		});
	},
	postWithAuthUploadCaseDoc(url, payload, customHeaders, customConfig) {
		let client = GetAxiosClientFORM();
		const authorizationHeader = {
			Authorization: 'Bearer ' + (sessionStorage.getItem('token') || ''),
		};
		let calculatedHeaders = customHeaders
			? { ...authorizationHeader, ...customHeaders }
			: authorizationHeader;
		return client.post(url, payload, {
			headers: calculatedHeaders,
			...customConfig,
			onUploadProgress: function (progressEvent) {
				let progress = parseInt(
					Math.round((progressEvent.loaded * 100) / progressEvent.total)
				);
				let result = {};
				result.index = payload.get('index');
				result.progress = progress;
				result.isEntityDoc = payload.get('isEntityDoc');
				bus.$emit('upload', result);
			}.bind(this),
		});
	},
	postWithAuthCaseBase64Doc(url, payload, customHeaders, customConfig) {
		let client = GetAxiosClient();
		const authorizationHeader = {
			Authorization: 'Bearer ' + (sessionStorage.getItem('token') || ''),
		};
		let calculatedHeaders = customHeaders
			? { ...authorizationHeader, ...customHeaders }
			: authorizationHeader;
		return client.post(url, payload, {
			headers: calculatedHeaders,
			...customConfig,
			onUploadProgress: function (progressEvent) {
				let progress = parseInt(
					Math.round((progressEvent.loaded * 100) / progressEvent.total)
				);
				let result = {};
				result.index = payload.index;
				result.progress = progress;
				result.isEntityDoc = payload.isEntityDoc;
				bus.$emit('upload', result);
			}.bind(this),
		});
	},
};

// TODO: Join these two lol
function GetAxiosClientFORM() {
	const axiosInstance = axios.create({
		baseURL: baseURL,
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});

	interceptors.forEach((interceptor) => {
		if (interceptor.request) {
			axiosInstance.interceptors.request.use(
				interceptor.request.config,
				interceptor.request.error
			);
		}
		if (interceptor.response) {
			axiosInstance.interceptors.response.use(
				interceptor.response.config,
				interceptor.response.error
			);
		}
	});

	return axiosInstance;
}

function GetAxiosClient() {
	const axiosInstance = axios.create({
		baseURL: baseURL,
		headers: {
			'Content-Type': 'application/json',
		},
	});

	interceptors.forEach((interceptor) => {
		if (interceptor.request) {
			axiosInstance.interceptors.request.use(
				interceptor.request.config,
				interceptor.request.error
			);
		}
		if (interceptor.response) {
			axiosInstance.interceptors.response.use(
				interceptor.response.config,
				interceptor.response.error
			);
		}
	});

	return axiosInstance;
}
